import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import Footer from '../../components/Footer';
import { Send, MessageCircle, Phone, Mail, HelpCircle, ChevronDown, ChevronUp, Clock, CheckCircle, Headphones } from 'lucide-react';

const Support = () => {
    const { isLoggedIn } = useAuth();
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('contact');
    const [expandedFAQ, setExpandedFAQ] = useState(null);
    const [formData, setFormData] = useState({
        subject: '',
        category: '',
        priority: 'medium',
        description: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitSuccess, setSubmitSuccess] = useState(false);

    React.useEffect(() => {
        if (isLoggedIn === null) return;
        if (!isLoggedIn) {
            navigate('/');
        }
    }, [isLoggedIn, navigate]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        setTimeout(() => {
            setIsSubmitting(false);
            setSubmitSuccess(true);
            setFormData({
                subject: '',
                category: '',
                priority: 'medium',
                description: ''
            });
            setTimeout(() => setSubmitSuccess(false), 5000);
        }, 1500);
    };

    const faqs = [
        {
            question: "How do I access my purchased courses?",
            answer: "You can access your purchased courses from the 'My Courses' section in your dashboard. Click on any course to start learning immediately."
        },
        {
            question: "Can I download course materials?",
            answer: "Yes, most course materials including PDFs, code files, and practice resources can be downloaded for offline learning."
        },
        {
            question: "How do I get my physical hardcopy certificate?",
            answer: "Physical hardcopy certificates are issued upon passing the NCVET assessment exam. You can apply for hardcopy delivery under the Apply Certificate section."
        },
        {
            question: "What payment methods do you accept?",
            answer: "We accept all major UPI apps, credit/debit cards, NetBanking, and digital wallets processed securely through Cashfree."
        },
        {
            question: "How do I reset my password?",
            answer: "You can reset your password from the Sign In modal by clicking 'Forgot password?' to receive a 6-digit verification code in your email."
        }
    ];

    const supportTickets = [
        {
            id: 'TICK-001',
            subject: 'Course video loading query',
            status: 'resolved',
            priority: 'high',
            createdAt: '2026-08-20',
            category: 'Technical'
        },
        {
            id: 'TICK-002',
            subject: 'Hardcopy certificate delivery status',
            status: 'in-progress',
            priority: 'medium',
            createdAt: '2026-08-25',
            category: 'Certificates'
        }
    ];

    const getStatusColor = (status) => {
        switch (status) {
            case 'resolved': return 'text-emerald-700 bg-emerald-100 border-emerald-300';
            case 'in-progress': return 'text-blue-700 bg-blue-100 border-blue-300';
            case 'pending': return 'text-amber-700 bg-amber-100 border-amber-300';
            default: return 'text-gray-700 bg-gray-100 border-gray-300';
        }
    };

    const getPriorityColor = (priority) => {
        switch (priority) {
            case 'high': return 'text-red-700 bg-red-100 border-red-300';
            case 'medium': return 'text-amber-700 bg-amber-100 border-amber-300';
            case 'low': return 'text-emerald-700 bg-emerald-100 border-emerald-300';
            default: return 'text-gray-700 bg-gray-100 border-gray-300';
        }
    };

    return (
        <div className="min-h-screen bg-gray-50/50 flex flex-col font-sans">
            {/* Success Toast Notification */}
            {submitSuccess && (
                <div className="fixed top-16 right-3 left-3 sm:left-auto bg-emerald-600 text-white px-4 py-3 rounded-xl shadow-xl z-50 animate-slideIn flex items-center gap-2.5">
                    <CheckCircle size={18} className="shrink-0 text-white" />
                    <span className="font-bold text-xs sm:text-sm">Support ticket submitted successfully! We'll reply within 24h.</span>
                </div>
            )}

            <main className="flex-1 max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-8 w-full space-y-4 sm:space-y-6">
                
                {/* Header Banner */}
                <div className="bg-gradient-to-r from-emerald-900 via-teal-900 to-gray-900 rounded-2xl sm:rounded-3xl p-4 sm:p-7 text-white shadow-md flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                    <div>
                        <div className="inline-flex items-center gap-1.5 bg-emerald-500/20 border border-emerald-400/30 text-emerald-300 text-[10px] sm:text-xs font-bold px-2.5 py-0.5 rounded-full uppercase tracking-wider mb-1.5">
                            <Headphones size={13} /> 24/7 Student Support
                        </div>
                        <h1 className="text-xl sm:text-3xl font-black">Support & Help Center</h1>
                        <p className="text-xs sm:text-sm text-emerald-100/90 mt-0.5">We are here to assist you with courses, certificates, and technical queries.</p>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
                    {/* Main Content Area */}
                    <div className="lg:col-span-2">
                        <div className="bg-white rounded-2xl sm:rounded-3xl shadow-sm border border-gray-100 p-3.5 sm:p-7">
                            
                            {/* Scrollable Tab Navigation Bar */}
                            <div className="flex gap-1.5 sm:gap-2 mb-4 sm:mb-6 border-b border-gray-200 pb-2 overflow-x-auto hide-scrollbar">
                                {[
                                    { id: 'contact', label: 'Contact Support', icon: MessageCircle },
                                    { id: 'faq', label: 'Frequently Asked Questions', icon: HelpCircle },
                                    { id: 'tickets', label: 'My Tickets', icon: Clock }
                                ].map(tab => (
                                    <button
                                        key={tab.id}
                                        onClick={() => setActiveTab(tab.id)}
                                        className={`flex items-center gap-1.5 px-3.5 sm:px-5 py-2 sm:py-2.5 rounded-xl text-xs sm:text-sm font-bold transition shrink-0 cursor-pointer ${activeTab === tab.id
                                                ? 'bg-emerald-600 text-white shadow-xs'
                                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                            }`}
                                    >
                                        <tab.icon size={16} />
                                        <span>{tab.label}</span>
                                    </button>
                                ))}
                            </div>

                            {/* Contact Support Form Tab */}
                            {activeTab === 'contact' && (
                                <form onSubmit={handleSubmit} className="space-y-4">
                                    <div className="border-b border-gray-100 pb-2.5">
                                        <h2 className="text-sm sm:text-lg font-bold text-gray-900">Submit a Support Ticket</h2>
                                        <p className="text-[11px] text-gray-500">Fill in the details below and our team will get back to you within 24 hours.</p>
                                    </div>

                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                                        <div>
                                            <label className="block text-[11px] sm:text-xs font-bold text-gray-700 uppercase mb-1">Subject *</label>
                                            <input
                                                type="text"
                                                name="subject"
                                                value={formData.subject}
                                                onChange={handleInputChange}
                                                required
                                                className="w-full px-3.5 py-2.5 border border-gray-300 rounded-xl text-xs sm:text-sm focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                                                placeholder="Brief description of your query"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-[11px] sm:text-xs font-bold text-gray-700 uppercase mb-1">Category *</label>
                                            <select
                                                name="category"
                                                value={formData.category}
                                                onChange={handleInputChange}
                                                required
                                                className="w-full px-3.5 py-2.5 border border-gray-300 rounded-xl text-xs sm:text-sm focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                                            >
                                                <option value="">Select Category</option>
                                                <option value="technical">Technical Issue</option>
                                                <option value="billing">Billing & Payments</option>
                                                <option value="courses">Course Access & Content</option>
                                                <option value="certificates">Hardcopy Certificate</option>
                                                <option value="account">Account & Login</option>
                                            </select>
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-[11px] sm:text-xs font-bold text-gray-700 uppercase mb-1">Priority Level</label>
                                        <select
                                            name="priority"
                                            value={formData.priority}
                                            onChange={handleInputChange}
                                            className="w-full px-3.5 py-2.5 border border-gray-300 rounded-xl text-xs sm:text-sm focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                                        >
                                            <option value="low">Low - General Question</option>
                                            <option value="medium">Medium - Normal Issue</option>
                                            <option value="high">High - Urgent Technical Problem</option>
                                        </select>
                                    </div>

                                    <div>
                                        <label className="block text-[11px] sm:text-xs font-bold text-gray-700 uppercase mb-1">Detailed Description *</label>
                                        <textarea
                                            name="description"
                                            value={formData.description}
                                            onChange={handleInputChange}
                                            required
                                            rows="4"
                                            className="w-full p-3 border border-gray-300 rounded-xl text-xs sm:text-sm focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                                            placeholder="Please describe your question or issue in detail..."
                                        />
                                    </div>

                                    <button
                                        type="submit"
                                        disabled={isSubmitting}
                                        className="w-full bg-emerald-600 text-white py-3 rounded-xl font-bold hover:bg-emerald-700 transition flex items-center justify-center gap-2 text-xs sm:text-sm shadow-md cursor-pointer disabled:opacity-60"
                                    >
                                        {isSubmitting ? (
                                            <>
                                                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                                                <span>Submitting Ticket...</span>
                                            </>
                                        ) : (
                                            <>
                                                <Send size={16} />
                                                <span>Submit Support Ticket</span>
                                            </>
                                        )}
                                    </button>
                                </form>
                            )}

                            {/* FAQ Tab */}
                            {activeTab === 'faq' && (
                                <div className="space-y-3">
                                    <div className="border-b border-gray-100 pb-2.5">
                                        <h2 className="text-sm sm:text-lg font-bold text-gray-900">Frequently Asked Questions</h2>
                                        <p className="text-[11px] text-gray-500">Quick answers to common questions</p>
                                    </div>

                                    <div className="space-y-2.5">
                                        {faqs.map((faq, index) => (
                                            <div key={index} className="border border-gray-200/80 rounded-xl overflow-hidden bg-gray-50/50">
                                                <button
                                                    onClick={() => setExpandedFAQ(expandedFAQ === index ? null : index)}
                                                    className="w-full px-3.5 py-3 text-left flex items-center justify-between hover:bg-gray-100/60 transition gap-2"
                                                >
                                                    <span className="font-bold text-xs sm:text-sm text-gray-900">{faq.question}</span>
                                                    {expandedFAQ === index ? (
                                                        <ChevronUp className="text-emerald-600 shrink-0" size={16} />
                                                    ) : (
                                                        <ChevronDown className="text-gray-400 shrink-0" size={16} />
                                                    )}
                                                </button>
                                                {expandedFAQ === index && (
                                                    <div className="px-3.5 pb-3 pt-1 border-t border-gray-200/40 text-xs sm:text-sm text-gray-600 leading-relaxed">
                                                        {faq.answer}
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Tickets Tab */}
                            {activeTab === 'tickets' && (
                                <div className="space-y-3">
                                    <div className="border-b border-gray-100 pb-2.5">
                                        <h2 className="text-sm sm:text-lg font-bold text-gray-900">My Support Tickets</h2>
                                        <p className="text-[11px] text-gray-500">Track status of submitted queries</p>
                                    </div>

                                    <div className="space-y-2.5">
                                        {supportTickets.map(ticket => (
                                            <div key={ticket.id} className="border border-gray-200/80 rounded-xl p-3.5 bg-gray-50/40 space-y-2">
                                                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
                                                    <div>
                                                        <h3 className="font-bold text-xs sm:text-sm text-gray-900">{ticket.subject}</h3>
                                                        <p className="text-[11px] text-gray-500">#{ticket.id} • {ticket.category}</p>
                                                    </div>
                                                    <div className="flex gap-1.5 shrink-0">
                                                        <span className={`px-2 py-0.5 border rounded-md text-[10px] font-bold uppercase tracking-wider ${getStatusColor(ticket.status)}`}>
                                                            {ticket.status}
                                                        </span>
                                                        <span className={`px-2 py-0.5 border rounded-md text-[10px] font-bold uppercase tracking-wider ${getPriorityColor(ticket.priority)}`}>
                                                            {ticket.priority}
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-2 text-[11px] text-gray-500 pt-1 border-t border-gray-100">
                                                    <Clock size={13} /> Created: {ticket.createdAt}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Right Sidebar Info */}
                    <div className="lg:col-span-1 space-y-4">
                        {/* Direct Channels */}
                        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 space-y-3">
                            <h3 className="text-xs sm:text-sm font-extrabold text-gray-900 uppercase tracking-wider border-b border-gray-100 pb-2">
                                Direct Contact Channels
                            </h3>

                            <div className="space-y-3">
                                <div className="flex items-center gap-3 p-2.5 bg-emerald-50/60 rounded-xl border border-emerald-100">
                                    <div className="w-9 h-9 bg-emerald-600 text-white rounded-xl flex items-center justify-center shrink-0">
                                        <Mail size={18} />
                                    </div>
                                    <div className="min-w-0">
                                        <p className="font-bold text-xs text-gray-900">Email Support</p>
                                        <p className="text-[11px] text-gray-600 truncate">support@learninghub.ind.in</p>
                                    </div>
                                </div>

                                <div className="flex items-center gap-3 p-2.5 bg-blue-50/60 rounded-xl border border-blue-100">
                                    <div className="w-9 h-9 bg-blue-600 text-white rounded-xl flex items-center justify-center shrink-0">
                                        <Phone size={18} />
                                    </div>
                                    <div className="min-w-0">
                                        <p className="font-bold text-xs text-gray-900">Phone Support</p>
                                        <p className="text-[11px] text-gray-600 truncate">+91 7605812086</p>
                                    </div>
                                </div>

                                <div className="flex items-center gap-3 p-2.5 bg-purple-50/60 rounded-xl border border-purple-100">
                                    <div className="w-9 h-9 bg-purple-600 text-white rounded-xl flex items-center justify-center shrink-0">
                                        <MessageCircle size={18} />
                                    </div>
                                    <div className="min-w-0">
                                        <p className="font-bold text-xs text-gray-900">Working Hours</p>
                                        <p className="text-[11px] text-gray-600 truncate">Mon - Sat: 9 AM - 7 PM</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Estimated Response Times */}
                        <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl p-4 border border-emerald-200/80 space-y-2">
                            <h3 className="text-xs sm:text-sm font-bold text-gray-900 flex items-center gap-2">
                                <Clock size={16} className="text-emerald-600" /> Response Time
                            </h3>
                            <div className="space-y-1.5 text-[11px]">
                                <div className="flex justify-between border-b border-emerald-200/50 pb-1">
                                    <span className="text-gray-600">Email Inquiry:</span>
                                    <span className="font-bold text-emerald-800">Within 24 hours</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Phone Call:</span>
                                    <span className="font-bold text-emerald-800">Immediate / 1 hour</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </main>

            <Footer />
        </div>
    );
};

export default Support;
